import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Bell, Check, MessageCircle, CalendarCheck2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const KIND_ICON = { comment: MessageCircle, rsvp: CalendarCheck2 };

function relTime(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    timer.current = setInterval(load, 25000);
    return () => timer.current && clearInterval(timer.current);
  }, [user]);

  const openAndMark = async () => {
    setOpen(true);
    if (unread > 0) {
      try {
        await api.post("/notifications/read-all");
        setUnread(0);
        // keep read state visible in list
        setItems((it) => it.map((n) => ({ ...n, read: true })));
      } catch { /* silent */ }
    }
  };

  const goTo = (n) => {
    setOpen(false);
    if (n.ref_type === "post") navigate("/feed");
    else if (n.ref_type === "event") navigate("/events");
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={(v) => (v ? openAndMark() : setOpen(false))}>
      <PopoverTrigger asChild>
        <button
          data-testid="bell-trigger"
          aria-label="Notifications"
          className="jirani-pill relative grid h-9 w-9 place-items-center rounded-full border border-black/5 bg-white text-[#2E473B] hover:bg-[#F9F7F4]"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="bell-unread-count"
              className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-[#D95D39] px-1 text-[10px] font-bold text-white ring-2 ring-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="bell-popover"
        align="end"
        className="w-[340px] rounded-2xl border-black/5 p-0"
      >
        <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
          <div className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
            Notifications
          </div>
          {items.length > 0 && (
            <button
              data-testid="bell-mark-all-read"
              onClick={async () => {
                await api.post("/notifications/read-all");
                setUnread(0);
                setItems((it) => it.map((n) => ({ ...n, read: true })));
              }}
              className="inline-flex items-center gap-1 text-xs text-[#2E473B] hover:text-[#D95D39]"
            >
              <Check className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {items.length === 0 ? (
            <div
              data-testid="bell-empty"
              className="px-4 py-10 text-center text-sm text-[#4A4A4A]"
            >
              You're all caught up. Karibu!
            </div>
          ) : (
            <ul className="divide-y divide-black/5">
              {items.map((n) => {
                const Icon = KIND_ICON[n.kind] || Bell;
                const initial = (n.actor_name || "?").charAt(0).toUpperCase();
                return (
                  <li key={n.id}>
                    <button
                      data-testid={`bell-item-${n.id}`}
                      onClick={() => goTo(n)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F9F7F4] ${
                        n.read ? "" : "bg-[#F4A261]/10"
                      }`}
                    >
                      <div className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#2E473B] text-xs font-semibold text-white">
                        {initial}
                        <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-[#D95D39] text-white">
                          <Icon className="h-2.5 w-2.5" />
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-[#1A1A1A]">
                          <span className="font-semibold">{n.actor_name || "A neighbor"}</span>{" "}
                          <span className="text-[#4A4A4A]">{n.message}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-[#2E473B]/70">
                          {relTime(n.created_at)}
                        </div>
                      </div>
                      {!n.read && (
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#D95D39]" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
